/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { useCallback, useLayoutEffect, useRef } from 'react';
import {
	type GicOptInStatusResourceData,
	gicOptInStatusResources,
} from '@jira/router-resources__global-issue-create-switch/src/gicOptInStatusResources.tsx';
import { NavigationCreateIssueButtonWrapperNudgeAsync } from '@jira/software__onboarding-nudges/src/NavigationCreateIssueButtonWrapperNudgeAsync.tsx';
import { NAVIGATION_ITEM_ID } from '@jira/navigation-apps__atlassian-navigation-ui/src/constants.tsx';
import { Create as AkCreateDI } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/create-button/index.tsx';
import { TopLevelErrorBoundary } from '@jira/navigation-apps__atlassian-navigation-ui/src/ui/error-boundary/index.tsx';
import { isSupportedUserAgent as isMobileUserAgent } from '@jira/platform__is-mobile-user-agent/src/index.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { useIsInitialLoadLegacyIssueCreate as useIsInitialLoadLegacyIssueCreateDI } from '@jira/platform__legacy-create-issue-legacy/src/controllers/is-initial-load-create-issue-legacy/index.tsx';
import OnCreateClickDI from '@jira/platform__legacy-create-issue-legacy/src/index.tsx';
import { GLOBAL_CREATE_BUTTON } from '@jira/platform__legacy-create-issue-legacy/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useInitializePersistentIssueCreateModalView } from '@jira/issue-create__issue-create-modal/src/controllers/use-initialize-persistent-issue-create-modal-view/index.tsx';
import { testIdGenerate } from '@jira/navigation-apps__common/src/utils/test-id.tsx';
import {
	GENERIC_GLOBAL_CREATE_BUTTON,
	MOBILE_GLOBAL_CREATE_BUTTON,
} from '@jira/issue-create__use-trigger-issue-create-modal/src/constants.tsx';
import { useTriggerIssueCreateModal } from '@jira/issue-create__use-trigger-issue-create-modal/src/main.tsx';
import type { TriggerPointKeyType } from '@jira/issue-create__use-trigger-issue-create-modal/src/types.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { useNavigationItemAnalytics as useNavigationItemAnalyticsDI } from '../../controllers/navigation-item-analytics/main.tsx';
import { GlobalIssueCreateFromTopNav } from '../../experiences.tsx';
import type { CreateProps } from './types.tsx';
import { shouldDisableGICFlagCreation } from './utils.tsx';

const create = defineMessage({
	defaultMessage: 'Create',
	id: 'atlassian-navigation.create.create',
	description:
		'Label for the create button in the top navigation bar that opens the issue creation dialog',
});

const createButtonAriaLabel = defineMessage({
	defaultMessage: 'Create issue',
	id: 'atlassian-navigation.create.create-button-aria-label',
	description:
		'Accessible label for the create button in the top navigation bar that opens the issue creation dialog',
});

const createButtonAriaLabelIssueTermRefresh = defineMessage({
	defaultMessage: 'Create',
	id: 'atlassian-navigation.create.create-button-aria-label-issue-term-refresh',
	description:
		'Accessible label for the create button in the top navigation bar, using refreshed terminology without the word issue',
});

const noop = () => undefined;

const Create = ({
	AkCreate = AkCreateDI,
	NudgeTooltip = NavigationCreateIssueButtonWrapperNudgeAsync,
	onCreateClick = OnCreateClickDI,
	useNavigationItemAnalytics = useNavigationItemAnalyticsDI,
	// @ts-expect-error - TS2339 - Property 'useIsInitialLoadLegacyIssueCreate' does not exist on type 'CreateProps'.
	useIsInitialLoadLegacyIssueCreate = useIsInitialLoadLegacyIssueCreateDI,
}: CreateProps) => {
	const testId = testIdGenerate('create');
	const { formatMessage } = useIntl();
	const [, { openIssueCreateModal }] = useTriggerIssueCreateModal();
	useInitializePersistentIssueCreateModalView();

	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.CREATE,
	});
	const [{ isInitialLoad }, actions] = useIsInitialLoadLegacyIssueCreate();
	let currentIssueCreateVisibility: GicOptInStatusResourceData['gicOptInStatus'] | null = null;
	const gicOptInStateOverridden = useRef(false);
	const isAnonymous = useIsAnonymous();

	const isMobileExperienceEnabled = isMobileUserAgent();

	// TODO: delete this code for clean up issue-create-stop-using-gicoptinstatusresources
	if (fg('render_modern_global_issue_create_modal_experience')) {
		// oxlint-disable-next-line react-hooks/rules-of-hooks
		const { data, update } = useResource(gicOptInStatusResources);

		// oxlint-disable-next-line react-hooks/rules-of-hooks
		useLayoutEffect(() => {
			if (isAnonymous && !gicOptInStateOverridden.current) {
				update(() => ({
					gicOptInStatus: {
						isEnabled: true,
					},
				}));
				gicOptInStateOverridden.current = true;
			}
		}, [isAnonymous, update]);

		currentIssueCreateVisibility = data && data?.gicOptInStatus;
	}

	const onClick = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
		(e) => {
			if (fg('render_modern_global_issue_create_modal_experience')) {
				if (fg('issue-create-stop-using-gicoptinstatusresources')) {
					GlobalIssueCreateFromTopNav.abort({ force: true });
					GlobalIssueCreateFromTopNav.start();
					GlobalIssueCreateFromTopNav.addMetadata({
						inputDelay: Math.round(performance.now() - e.timeStamp),
					});

					const nextState: {
						disableCreationSuccessFlag: boolean;
						triggerPointKey: TriggerPointKeyType;
						displayMinimizableButtons?: boolean;
						displayCreateAnotherIssueToggle?: boolean;
					} = {
						disableCreationSuccessFlag: shouldDisableGICFlagCreation(),
						triggerPointKey: GENERIC_GLOBAL_CREATE_BUTTON,
						...(isMobileExperienceEnabled
							? {
									displayMinimizableButtons: false,
									displayCreateAnotherIssueToggle: false,
									triggerPointKey: MOBILE_GLOBAL_CREATE_BUTTON,
								}
							: {}),
					};
					openIssueCreateModal(nextState);
				}
				// TODO: delete this code clean up issue-create-stop-using-gicoptinstatusresources
				else if (currentIssueCreateVisibility !== null) {
					if (currentIssueCreateVisibility.isEnabled) {
						GlobalIssueCreateFromTopNav.abort({ force: true });
						GlobalIssueCreateFromTopNav.start();
						GlobalIssueCreateFromTopNav.addMetadata({
							inputDelay: Math.round(performance.now() - e.timeStamp),
						});

						const nextState: {
							disableCreationSuccessFlag: boolean;
							triggerPointKey: TriggerPointKeyType;
							displayMinimizableButtons?: boolean;
							displayCreateAnotherIssueToggle?: boolean;
						} = {
							disableCreationSuccessFlag: shouldDisableGICFlagCreation(),
							triggerPointKey: GENERIC_GLOBAL_CREATE_BUTTON,
							...(isMobileExperienceEnabled
								? {
										displayMinimizableButtons: false,
										displayCreateAnotherIssueToggle: false,
										triggerPointKey: MOBILE_GLOBAL_CREATE_BUTTON,
									}
								: {}),
						};
						openIssueCreateModal(nextState);
					} else {
						onCreateClick({ isInitialLoad, triggerSource: GLOBAL_CREATE_BUTTON });
						if (isInitialLoad) {
							actions.clearInitialLoaded();
						}
					}
				}
				// TODO: delete this code after issue-create-stop-using-gicoptinstatusresources
				else {
					log.safeErrorWithoutCustomerData(
						'global-issue-create',
						'visibility data is still getting downloaded',
					);
				}
			} else {
				onCreateClick({ isInitialLoad, triggerSource: GLOBAL_CREATE_BUTTON });
				if (isInitialLoad) {
					actions.clearInitialLoaded();
				}
			}

			triggerAnalytics();
		},
		[
			actions,
			currentIssueCreateVisibility,
			isInitialLoad,
			isMobileExperienceEnabled,
			onCreateClick,
			openIssueCreateModal,
			triggerAnalytics,
		],
	);

	if (!__SERVER__) {
		return (
			<NudgeTooltip>
				<AkCreate
					onClick={onClick}
					text={formatMessage(create)}
					testId={testId}
					label={formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? createButtonAriaLabelIssueTermRefresh
							: createButtonAriaLabel,
					)}
				/>
			</NudgeTooltip>
		);
	}

	return (
		<AkCreate
			onClick={noop}
			text={formatMessage(create)}
			testId={testId}
			label={formatMessage(
				fg('jira-issue-terminology-refresh-m3')
					? createButtonAriaLabelIssueTermRefresh
					: createButtonAriaLabel,
			)}
		/>
	);
};

const CreateWithErrorBoundary = (props: CreateProps): React.JSX.Element => (
	<TopLevelErrorBoundary id={NAVIGATION_ITEM_ID.CREATE}>
		<Create {...props} />
	</TopLevelErrorBoundary>
);

export default CreateWithErrorBoundary;
